import { accessToken, login } from '@/auth/oidc'
import { ApiError, BASE_URL, request } from '@/lib/api'

/**
 * Обёртки над `/api/v1/sales/price-lists` и `/api/v1/products/{id}/prices`.
 *
 * Сервер отдаёт JSON с `non_null`: незаполненного поля в ответе нет вовсе, поэтому
 * необязательные поля объявлены `?`, а не `| null`. Деньги приходят числом в валюте прайса;
 * валюта одна на прайс, у строки её нет.
 */

export const PRICE_LISTS_KEY = ['price-lists']
export const RESOURCE = 'sales.pricelist'

export interface PriceList {
  id: string
  version: number
  code: string
  name: string
  currency: string
  priority: number
  validFrom?: string
  validUntil?: string
  active: boolean
  /** Прайс по умолчанию в своей валюте (V61): им оценивается строка без цены. */
  defaultList: boolean
  items: number
  /** Клиентов, которым прайс назначен: такой прайс не удаляется. */
  counterparties: number
  effectiveToday: boolean
}

export interface PriceListInput {
  code?: string
  name: string
  currency: string
  priority: number
  validFrom: string | null
  validUntil: string | null
  active: boolean
  defaultList: boolean
  version?: number
}

export interface PriceItem {
  id: string
  productId: string
  sku: string
  productName: string
  uom?: string
  productActive: boolean
  price: number
  /** Цена действует от этого количества; 0 — базовый уровень. */
  minQuantity: number
  discountPercent: number
}

export interface PriceItemPage {
  items: PriceItem[]
  /** Нет на последней странице. */
  nextCursor?: string
  total: number
}

export interface BulkRefusal {
  productId?: string
  code?: string
  message: string
}

export interface BulkResult {
  added: PriceItem[]
  refused: BulkRefusal[]
}

export interface ImportRowError {
  line: number
  column?: string
  message: string
}

export interface PriceImportReport {
  totalRows: number
  imported: number
  created: number
  updated: number
  failed: number
  errors: ImportRowError[]
}

export interface ProductPrice {
  priceListId: string
  priceListCode: string
  priceListName: string
  currency: string
  defaultList: boolean
  priority: number
  price: number
  discountPercent: number
  /** Цена после скидки строки прайса — её и подставит документ. */
  effectivePrice: number
  minQuantity: number
}

export interface ProductPrices {
  on: string
  prices: ProductPrice[]
}

const base = '/sales/price-lists'

export const fetchPriceLists = (signal?: AbortSignal) =>
  request<{ priceLists: PriceList[] }>(base, { signal }).then((r) => r.priceLists)

export const fetchPriceList = (id: string, signal?: AbortSignal) =>
  request<PriceList>(`${base}/${encodeURIComponent(id)}`, { signal })

export const createPriceList = (body: PriceListInput) =>
  request<PriceList>(base, { method: 'POST', body })

export const updatePriceList = (id: string, body: PriceListInput) =>
  request<PriceList>(`${base}/${encodeURIComponent(id)}`, { method: 'PUT', body })

export const deletePriceList = (id: string) =>
  request<void>(`${base}/${encodeURIComponent(id)}`, { method: 'DELETE' })

export function fetchPriceItems(
  id: string,
  query: { search?: string; cursor?: string | null; limit?: number },
  signal?: AbortSignal,
) {
  const params = new URLSearchParams()
  if (query.search) params.set('search', query.search)
  if (query.cursor) params.set('cursor', query.cursor)
  if (query.limit) params.set('limit', String(query.limit))
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return request<PriceItemPage>(`${base}/${encodeURIComponent(id)}/items${suffix}`, { signal })
}

export const addPriceItems = (id: string, items: { productId: string; price: number }[]) =>
  request<BulkResult>(`${base}/${encodeURIComponent(id)}/items/bulk`, { method: 'POST', body: { items } })

export const updatePriceItem = (id: string, itemId: string, price: number) =>
  request<PriceItem>(`${base}/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`, {
    method: 'PUT',
    body: { price },
  })

export const deletePriceItem = (id: string, itemId: string) =>
  request<void>(`${base}/${encodeURIComponent(id)}/items/${encodeURIComponent(itemId)}`, { method: 'DELETE' })

/**
 * Multipart вручную, мимо `request`: границу multipart браузер вычисляет сам, и заданный
 * руками `Content-Type` сделал бы тело неразбираемым (как в `lists/importApi.ts`).
 */
export async function importPrices(id: string, file: File): Promise<PriceImportReport> {
  const token = await accessToken()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const body = new FormData()
  body.append('file', file)
  const response = await fetch(`${BASE_URL}${base}/${encodeURIComponent(id)}/items/import`, {
    method: 'POST',
    headers,
    body,
  })
  if (response.status === 401) {
    await login()
    throw new ApiError(401, 'unauthorized', 'Сессия истекла')
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => null)
    throw new ApiError(response.status, problem?.code ?? null, problem?.detail ?? problem?.message ?? response.statusText)
  }
  return (await response.json()) as PriceImportReport
}

export const fetchProductPrices = (productId: string, on?: string, signal?: AbortSignal) =>
  request<ProductPrices>(
    `/products/${encodeURIComponent(productId)}/prices${on ? `?on=${encodeURIComponent(on)}` : ''}`,
    { signal },
  )
